/**
 * Login verification page.
 * 
 * Handles magic link verification from login email.
 * Shows loading, success with redirect, or safe error state.
 * 
 * Security posture:
 * - Does not expose internal token validation reasons
 * - Uses safe generic error messages for all failure cases
 * - Does not enumerate account existence
 * - On success, hydrates auth state from backend before redirect
 * 
 * Post-login hydration flow:
 * completeLogin() -> getSession() -> hydrateFromSession() -> redirect
 * This ensures sponsor access state is immediately available after login.
 */
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { completeLogin, getSession, AuthError } from '@/api/auth';
import { useAuthStore } from '@/store/authStore';
import { RoutePaths } from '@/routes';

type LoginVerifyState = 'loading' | 'success' | 'error';

export function LoginVerifyPage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<LoginVerifyState>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setState('error');
      setErrorMessage('Login link is invalid or missing. Please request a new one.');
      return;
    }

    // Complete login with the magic link token
    // Backend sets session cookie on success
    // Hydrate auth state from backend before redirect to avoid stale UI
    completeLogin(token)
      .then(async () => {
        try {
          // Hydrate auth state immediately after successful login
          // This ensures sponsor access state is available without waiting for app load
          const session = await getSession();
          if (session) {
            const hydrateFromSession = useAuthStore.getState().hydrateFromSession;
            hydrateFromSession(session);
          }
        } catch (hydrationError) {
          // Log but don't fail - bootstrap will retry on app load
          console.warn('Post-login hydration failed, will retry on app load:', hydrationError);
        }
        
        setState('success');
        // Redirect to home after short delay
        setTimeout(() => {
          window.location.href = RoutePaths.HOME;
        }, 1500);
      })
      .catch((err) => {
        setState('error');
        if (err instanceof AuthError && err.safe) {
          // Show safe generic message - do not expose internal reasons
          setErrorMessage('This login link is invalid or has expired. Please request a new one.');
        } else {
          // Generic message for unknown errors
          setErrorMessage('Login failed. Please request a new login email.');
        }
      });
  }, [searchParams]);

  if (state === 'loading') {
    return (
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Verifying your login…</h1>
          <p className="page-description">
            Please wait while we sign you in.
          </p>
        </div>
        <div className="loading-indicator">
          <div className="spinner" />
        </div>
        <style>{`
          .loading-indicator {
            display: flex;
            justify-content: center;
            padding: 40px 0;
          }
          .spinner {
            width: 40px;
            height: 40px;
            border: 3px solid var(--color-border);
            border-top-color: var(--color-accent);
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Login failed</h1>
        </div>
        <div className="error-box" role="alert">
          <p className="error-text">{errorMessage}</p>
        </div>
        <div className="action-buttons">
          <Link to={RoutePaths.LOGIN} className="btn btn-secondary">
            Request new login email
          </Link>
          <Link to={RoutePaths.REGISTER} className="btn btn-link">
            Create an account
          </Link>
        </div>
        <style>{`
          .error-box {
            padding: 20px;
            background-color: rgba(233, 69, 96, 0.1);
            border: 1px solid var(--color-accent);
            border-radius: var(--radius);
            margin-bottom: var(--spacing);
          }
          .error-text {
            color: var(--color-text);
            margin: 0;
          }
          .action-buttons {
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-top: var(--spacing);
          }
          .btn {
            display: block;
            width: 100%;
            padding: 14px 24px;
            border-radius: var(--radius);
            font-size: 16px;
            font-weight: 600;
            text-align: center;
            text-decoration: none;
            transition: opacity 0.2s ease;
          }
          .btn:hover {
            opacity: 0.9;
          }
          .btn-secondary {
            background-color: var(--color-accent);
            color: white;
          }
          .btn-link {
            background: none;
            color: var(--color-accent);
            border: none;
          }
        `}</style>
      </div>
    );
  }

  // Success state - redirecting
  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Login successful</h1>
      </div>
      <div className="success-box">
        <p className="success-title">Welcome back!</p>
        <p className="success-text">
          You are now signed in. Redirecting you to the app…
        </p>
      </div>
      <style>{`
        .success-box {
          padding: 20px;
          background-color: rgba(46, 204, 113, 0.1);
          border: 1px solid #2ecc71;
          border-radius: var(--radius);
          margin-bottom: var(--spacing);
          text-align: center;
        }
        .success-title {
          font-size: 1.2rem;
          font-weight: 600;
          margin: 0 0 8px 0;
          color: #2ecc71;
        }
        .success-text {
          color: var(--color-text-muted);
          margin: 0;
        }
      `}</style>
    </div>
  );
}